/* Accessible, dependency-free homepage carousel. Auto-rotates across screen sizes,
   stops after manual interaction, and remains usable with touch or a keyboard. */
(() => {
  'use strict';

  const initialized = new WeakSet();

  const init = (hero) => {
    if (initialized.has(hero)) return;
    initialized.add(hero);

    const gallery = hero.querySelector('[data-hero-gallery]');
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const thumbs = [...hero.querySelectorAll('[data-hero-dot]')];
    const previous = hero.querySelector('[data-hero-prev]');
    const next = hero.querySelector('[data-hero-next]');
    const toggle = hero.querySelector('[data-hero-toggle]');
    const toggleIcon = hero.querySelector('[data-hero-toggle-icon]');
    const toggleLabel = hero.querySelector('[data-hero-toggle-label]');
    const current = hero.querySelector('[data-hero-current]');
    const status = hero.querySelector('[data-hero-status]');

    if (!gallery || slides.length < 2) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplayEnabled = hero.dataset.autoplay === 'true';
    const interval = Math.max(Number(hero.dataset.interval) || 4000, 4000);

    let activeIndex = 0;
    let timer = null;
    let manualPause = false;
    let touchStartX = null;
    let touchStartY = null;

    const formatIndex = (index) => String(index + 1).padStart(2, '0');

    const updateToggle = () => {
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', manualPause ? 'true' : 'false');
      if (toggleIcon) toggleIcon.textContent = manualPause ? '▶' : 'Ⅱ';
      if (toggleLabel) toggleLabel.textContent = manualPause ? 'Play carousel' : 'Pause carousel';
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const canAutoplay = () => (
      autoplayEnabled
      && !reduceMotion.matches
      && !manualPause
      && !document.hidden
    );

    const goTo = (index, announce = false) => {
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === activeIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (current) current.textContent = formatIndex(activeIndex);

      if (announce && status) {
        const label = slides[activeIndex].dataset.slideLabel || `Image ${activeIndex + 1}`;
        status.textContent = `${label}, ${activeIndex + 1} of ${slides.length}`;
      }
    };

    const start = () => {
      stop();
      if (!canAutoplay()) return;
      timer = window.setInterval(() => goTo(activeIndex + 1), interval);
    };

    const pauseAfterInteraction = () => {
      manualPause = true;
      stop();
      updateToggle();
    };

    const moveManually = (offset) => {
      pauseAfterInteraction();
      goTo(activeIndex + offset, true);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        pauseAfterInteraction();
        goTo(index, true);
      });
    });

    previous?.addEventListener('click', () => moveManually(-1));
    next?.addEventListener('click', () => moveManually(1));

    toggle?.addEventListener('click', () => {
      manualPause = !manualPause;
      updateToggle();
      if (manualPause) stop();
      else start();
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      moveManually(event.key === 'ArrowLeft' ? -1 : 1);
    });

    gallery.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
      touchStartY = event.changedTouches[0]?.clientY ?? null;
    }, { passive: true });

    gallery.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
      const touchEndY = event.changedTouches[0]?.clientY ?? touchStartY ?? 0;
      const distanceX = touchEndX - touchStartX;
      const distanceY = touchEndY - (touchStartY ?? touchEndY);
      touchStartX = null;
      touchStartY = null;

      // A mostly vertical gesture belongs to the page, not the carousel.
      if (Math.abs(distanceX) < 45 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.15) return;
      moveManually(distanceX > 0 ? -1 : 1);
    }, { passive: true });

    gallery.addEventListener('touchcancel', () => {
      touchStartX = null;
      touchStartY = null;
    }, { passive: true });

    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', (event) => {
      if (!hero.contains(event.relatedTarget)) start();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    reduceMotion.addEventListener?.('change', start);

    goTo(0);
    updateToggle();
    start();
  };

  const boot = (root = document) => root.querySelectorAll('[data-home-hero]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const selectedSlide = event.target.closest('[data-hero-slide]');
    if (!selectedSlide) return;
    const hero = selectedSlide.closest('[data-home-hero]');
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    hero.querySelectorAll('[data-hero-dot]')[slides.indexOf(selectedSlide)]?.click();
  });
})();
